// packages/shared-hooks/src/users/useUsersList.ts
// Spec §18. Consumer passes `UserListFilter` (role + active); boolean `active`
// is forwarded as a primitive and serialised by the client's query-string
// encoder. `signal` threads through for abort-on-unmount.
import { useQuery } from '@tanstack/react-query';
import { UserSchema, type User, type UserListFilter } from '@magimp/shared';

import { useApiClient } from '../api/context.js';

const UsersListResponseSchema = UserSchema.array();

export interface UseUsersListOptions {
  /** When false, the query is parked (no `/users` request fires). Defaults
   *  to true. Use this for admin-only consumers (e.g. an admin manager
   *  filter) so non-admin actors do not generate stray 403s on a
   *  `users.manage`-gated endpoint. */
  enabled?: boolean;
}

export function useUsersList(filter: UserListFilter = {}, options: UseUsersListOptions = {}) {
  const client = useApiClient();
  const enabled = options.enabled ?? true;
  return useQuery<User[]>({
    queryKey: ['users', 'list', filter],
    queryFn: ({ signal }) =>
      client.get<User[]>(
        '/users',
        {
          signal,
          query: filter as Record<string, string | number | boolean | null | undefined>,
        },
        UsersListResponseSchema,
      ),
    enabled,
  });
}
